import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { createAPIEndPoint } from 'config/api/api';
import { getUserData } from 'utils';
import { formatFieldLabel } from 'utils/formatting';

export const ProvidersModal = ({
  isOpen,
  onClose,
  type = 'add',
  providerId,
  fetchAgain,
}) => {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? 1;
  const dashboard_id = userData?.dashboard?.id ?? null;
  // Validation Schema
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('Full Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    // phone: Yup.string()
    //   .matches(
    //     /^\(\d{3}\) \d{3}-\d{4}$/,
    //     'Phone number must be in format (XXX) XXX-XXXX',
    //   )
    //   .required('Phone number is required'),
    // email: Yup.string()
    //   .email('Invalid email format')
    //   .required('Email is required'),
    // designation: Yup.string().required('Designation is required'),
    NPI: Yup.string().required('NPI is required'),
    // TIN: Yup.string().required('TIN is required'),
    // stateId: Yup.string().required('State ID is required'),
    provider_code: Yup.string().required('Provider Code is required'),
  });

  const [providersData, setProvidersData] = useState({});
  const [loading, setLoading] = useState(false);

  console.log('providersData:', providersData);

  const fetchProvider = async () => {
    try {
      const response =
        await createAPIEndPoint('clinic_providers/').fetchById(providerId);
      setProvidersData(response.data.provider || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching providers');
    }
  };

  useEffect(() => {
    if (providerId && type === 'edit') {
      fetchProvider();
    }
  }, [providerId, type]);

  const handleCreate = async (values, { resetForm }) => {
    try {
      setLoading(true);

      const payload = {
        first_name: values.firstName || '',
        last_name: values.lastName || '',
        email: values.email,
        phone: values.phone,
        designation: values.designation,
        clinic_id, // Update if dynamic
        tin: values.TIN,
        npi: values.NPI,
        role_id: '2', // Update if dynamic
        state_id: values.stateId,
        provider_code: values.provider_code || '',
        dashboard_id,
      };

      console.log('Submitting payload:', payload);

      if (providerId) {
        // Update provider
        await createAPIEndPoint('clinic_providers/').update(
          providerId,
          payload,
        );
        toast.success('Provider updated successfully!');
      } else {
        // Create new provider
        await createAPIEndPoint('clinic_providers').create({
          ...payload,
          location_ids: [30],
        });
        toast.success('Provider created successfully!');
      }

      resetForm();
      onClose();
      setProvidersData({});
      fetchAgain();
    } catch (error) {
      setLoading(false);
      console.error('Error creating/updating provider:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'An unexpected error occurred.';
      toast.error(errorMessage);
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    const formatted = !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

    return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
  };

  // Formik Setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: providersData.first_name || '',
      lastName: providersData.last_name || '',
      phone: providersData?.phone || '',
      email: providersData?.email || '',
      designation: providersData?.designation || '',
      NPI: providersData?.npi || '',
      TIN: providersData?.tin || '',
      stateId: providersData?.state_id || '',
      provider_code: providersData?.provider_code || '',
    },
    validationSchema,
    onSubmit: handleCreate,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setProvidersData({});
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="16px"
        pb="16px"
        maxWidth={{ base: '90%', md: '448px' }}
        maxH="90vh"
        overflowY="auto"
      >
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Provider' : 'Create Provider'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody maxHeight="448px" overflowY="auto" pb={2}>
            {[
              'firstName',
              'lastName',
              'phone',
              'email',
              'designation',
              'provider_code',
              'NPI',
              'TIN',
              'stateId',
            ].map((field, index) => (
              <FormControl
                key={index}
                isInvalid={formik.touched[field] && formik.errors[field]}
                mb={2}
              >
                <FormLabel mb={0} textTransform="capitalize">
                  {/* {field.replace(/([A-Z])/g, ' $1').trim()} */}
                  {formatFieldLabel(field)}
                </FormLabel>
                <Input
                  name={field}
                  placeholder={`Enter ${formatFieldLabel(field).toLowerCase()}`}
                  // placeholder={`Enter ${field
                  //   .replace(/([A-Z])/g, ' $1')
                  //   .trim()
                  //   .toLowerCase()}`}
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  value={formik.values[field]}
                  maxLength={field === 'phone' ? 14 : undefined}
                  onChange={(e) => {
                    if (field === 'phone') {
                      formik.handleChange({
                        target: {
                          name: 'phone',
                          value: formatPhoneNumber(e.target.value),
                        },
                      });
                    } else {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched[field] && formik.errors[field] && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors[field]}
                  </Text>
                )}
              </FormControl>
            ))}
          </ModalBody>
          <ModalFooter pt={0}>
            <Button
              type="submit"
              colorScheme="brand"
              w="100%"
              mt={4}
              isLoading={formik.isSubmitting}
            >
              {type === 'edit' ? 'Edit Provider' : 'Create Provider'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
